import React, { useMemo, useState, useEffect } from "react";
import { Grid } from "@aws-amplify/ui-react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ConfirmModal from "components/utils/ConfirmModal";

ChartJS.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

const sampleData = {
  warehouses: [
    { id: "wh-1", name: "Almacén Central", active: true, shelves: ["A", "B", "C"], levels: ["1", "2", "3"] },
    { id: "wh-2", name: "Almacén Norte", active: true, shelves: ["D", "E"], levels: ["1", "2"] },
  ],
  supplies: [
    { id: "sup-1", label: "Urea 46%", active: true },
    { id: "sup-2", label: "Azufre en polvo", active: true },
    { id: "sup-3", label: "Piretroide X", active: true },
  ],
  supplyMasters: [
    { supplyId: "sup-1", activeIngredient: "Urea", unit: "kg", classification: "Fertilizante", type: "Sólido" },
    { supplyId: "sup-2", activeIngredient: "Azufre", unit: "kg", classification: "Insumo", type: "Sólido" },
    { supplyId: "sup-3", activeIngredient: "Piretroide", unit: "L", classification: "Insecticida", type: "Líquido" },
  ],
  // inventoryRecords: each record is a stock registration in a warehouse location
  inventoryRecords: [
    // Urea in Almacén Central
    { id: "rec-1", supplyId: "sup-1", warehouseId: "wh-1", date: "2025-10-01", person: "Juan Pérez", providerName: "AgroDistribuciones SA", shelf: "A", level: "2", availableQuantity: 120 },
    { id: "rec-2", supplyId: "sup-1", warehouseId: "wh-1", date: "2025-09-15", person: "María Gómez", providerName: "Fertilizantes Ltda", shelf: "B", level: "1", availableQuantity: 50 },
    // Azufre in Almacén Norte
    { id: "rec-3", supplyId: "sup-2", warehouseId: "wh-2", date: "2025-08-20", person: "Carlos Ruiz", providerName: "Suministros Agro", shelf: "D", level: "1", availableQuantity: 200 },
    // Piretroide in Almacén Central small lots
    { id: "rec-4", supplyId: "sup-3", warehouseId: "wh-1", date: "2025-10-10", person: "Ana López", providerName: "Insecticidas SA", shelf: "C", level: "3", availableQuantity: 30 },
    { id: "rec-5", supplyId: "sup-3", warehouseId: "wh-1", date: "2025-10-12", person: "Ana López", providerName: "Insecticidas SA", shelf: "A", level: "1", availableQuantity: 20 },
  ],
};

export default function ReportInputs(props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = props.data || sampleData || { warehouses: [], supplies: [], supplyMasters: [], inventoryRecords: [] };

  // minimal filters: warehouse + supply (native selects)
  const warehouses = useMemo(() => (data.warehouses || []).filter((w) => w.active), [data]);
  const supplies = useMemo(() => (data.supplies || []).filter((s) => s.active), [data]);

  const [selectedWarehouseId, setSelectedWarehouseId] = useState(warehouses[0]?.id || "");
  const [selectedSupplyId, setSelectedSupplyId] = useState(supplies[0]?.id || "");

  // sync defaults when lists change
  useEffect(() => setSelectedWarehouseId(warehouses[0]?.id || ""), [warehouses]);
  useEffect(() => setSelectedSupplyId(supplies[0]?.id || ""), [supplies]);

  // master info of selected supply
  const supplyMaster = useMemo(
    () => (data.supplyMasters || []).find((m) => m.supplyId === selectedSupplyId) || null,
    [data, selectedSupplyId]
  );

  // location rows for the selected supply in the selected warehouse
  const locationRows = useMemo(() => {
    if (!selectedSupplyId || !selectedWarehouseId) return [];
    return (data.inventoryRecords || [])
      .filter((r) => r.supplyId === selectedSupplyId && r.warehouseId === selectedWarehouseId)
      .map((r) => ({
        id: r.id,
        date: r.date,
        person: r.person,
        provider: r.providerName,
        shelf: r.shelf,
        level: r.level,
        availableQuantity: Number(r.availableQuantity ?? 0),
      }));
  }, [data, selectedSupplyId, selectedWarehouseId]);

  // available quantity in selected warehouse for this supply
  const availableQuantity = useMemo(() => locationRows.reduce((acc, r) => acc + (r.availableQuantity || 0), 0), [locationRows]);

  // For charts:
  // - Bar chart: availableQuantity per shelf (group by shelf) for selected supply & warehouse
  // - Pie chart: proportion of this supply's available quantity vs total supplies in the same warehouse (by classification)
  const groupedByShelf = useMemo(() => {
    const map = {};
    locationRows.forEach((r) => {
      const key = r.shelf ?? "N/A";
      map[key] = (map[key] || 0) + (r.availableQuantity || 0);
    });
    return map;
  }, [locationRows]);

  const totalByClassificationInWarehouse = useMemo(() => {
    // sum availableQuantity for each classification in the selected warehouse
    const map = {};
    const records = (data.inventoryRecords || []).filter((r) => r.warehouseId === selectedWarehouseId);
    records.forEach((r) => {
      const master = (data.supplyMasters || []).find((m) => m.supplyId === r.supplyId);
      const cls = master?.classification || "Otro";
      map[cls] = (map[cls] || 0) + Number(r.availableQuantity ?? 0);
    });
    return map;
  }, [data, selectedWarehouseId]);

  // chart labels/values
  const shelfLabels = useMemo(() => Object.keys(groupedByShelf), [groupedByShelf]);
  const shelfValues = useMemo(() => shelfLabels.map((k) => groupedByShelf[k]), [shelfLabels, groupedByShelf]);

  const classificationLabels = useMemo(() => Object.keys(totalByClassificationInWarehouse), [totalByClassificationInWarehouse]);
  const classificationValues = useMemo(() => classificationLabels.map((k) => totalByClassificationInWarehouse[k]), [classificationLabels, totalByClassificationInWarehouse]);

  // pastel palette
  const PASTEL_COLORS = [
    "#90d47dff",
    "#f09bb4ff",
    "#fcdc86ff",
    "#8eb4ffff",
    "#ce99f7ff",
    "#ffbe90ff",
    "#98eccdff",
  ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForShelves = useMemo(() => shelfLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [shelfLabels]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForClass = useMemo(() => classificationLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [classificationLabels]);

  const chartHeightPx = props.chartHeightPx || 360;

  const barData = useMemo(() => ({
    labels: shelfLabels,
    datasets: [
      {
        label: "Cantidad disponible",
        data: shelfValues,
        backgroundColor: colorsForShelves,
        borderColor: colorsForShelves,
        borderWidth: 1,
      },
    ],
  }), [shelfLabels, shelfValues, colorsForShelves]);

  const pieData = useMemo(() => ({
    labels: classificationLabels,
    datasets: [
      {
        data: classificationValues,
        backgroundColor: colorsForClass,
        hoverOffset: 6,
      },
    ],
  }), [classificationLabels, classificationValues, colorsForClass]);

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false, // width automatic, height controlled by container
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  const barOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: { position: "top" },
      title: { display: false, text: "Cantidad por Estante" },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const pieOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: { position: "bottom" },
      title: { display: false, text: "Distribución por Clasificación (almacén)" },
    },
  };

  // modals / placeholders
  const [modalDetails, setModalDetails] = useState({ visible: false, itemId: null, itemName: null });
  function handleConfirmDelete() {
    setModalDetails({ visible: false, itemId: null });
  }

  return (
    <div>
      <span className="fs-3">Inventario de Insumos</span>

      <div className="p-3">
        {/* filtros mínimos: Almacén + Insumo */}
        <Grid className="mb-3" templateColumns={{ medium: "1fr 1fr" }} rowGap="15px" columnGap="15px">
          <div>
            <label className="form-label">Almacén</label>
            <select className="form-select" value={selectedWarehouseId} onChange={(e) => setSelectedWarehouseId(e.target.value)}>
              {(warehouses || []).map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Insumo</label>
            <select className="form-select" value={selectedSupplyId} onChange={(e) => setSelectedSupplyId(e.target.value)}>
              {(supplies || []).map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </Grid>

        {/* tarjetas mínimas según reporte */}
        <CRow className="mb-3">
          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Etiqueta</CCardHeader>
              <CCardBody>
                <div className="fs-5">{supplies.find((s) => s.id === selectedSupplyId)?.label ?? "—"}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Ingrediente activo / Unidad</CCardHeader>
              <CCardBody>
                <div className="fs-6">{supplyMaster?.activeIngredient ?? "—"} / {supplyMaster?.unit ?? "—"}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Clasificación / Tipo / Disponible</CCardHeader>
              <CCardBody>
                <div className="fs-6">{supplyMaster?.classification ?? "—"} / {supplyMaster?.type ?? "—"}</div>
                <div className="mt-2 fw-bold">{availableQuantity} {supplyMaster?.unit ?? ""}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* gráficos: Barras (por estante) y Torta (por clasificación en almacén) */}
        <CRow>
          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Cantidad disponible por Estante</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {shelfLabels.length === 0 ? (
                  <div className="text-muted">No hay ubicaciones para este insumo en el almacén seleccionado.</div>
                ) : (
                  <Bar data={barData} options={barOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Distribución por Clasificación (almacén)</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {classificationLabels.length === 0 ? (
                  <div className="text-muted">No hay datos en el almacén seleccionado.</div>
                ) : (
                  <Pie data={pieData} options={pieOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* Ubicación con producto en Almacén (tabla) */}
        <CRow>
          <CCol>
            <CCard>
              <CCardHeader>Ubicación con producto en Almacén</CCardHeader>
              <CCardBody>
                <div className="table-responsive">
                  <CTable hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Fecha de Registro</CTableHeaderCell>
                        <CTableHeaderCell>Persona que realizó el Procedimiento</CTableHeaderCell>
                        <CTableHeaderCell>Nombre del Proveedor</CTableHeaderCell>
                        <CTableHeaderCell>Estante</CTableHeaderCell>
                        <CTableHeaderCell>Nivel</CTableHeaderCell>
                        <CTableHeaderCell>Cantidad Disponible</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {locationRows.length === 0 ? (
                        <CTableRow>
                          <CTableDataCell colSpan={6} className="text-muted">No hay registros de inventario para este insumo en el almacén seleccionado.</CTableDataCell>
                        </CTableRow>
                      ) : (
                        locationRows.map((row) => (
                          <CTableRow key={row.id}>
                            <CTableDataCell>{row.date ?? "-"}</CTableDataCell>
                            <CTableDataCell>{row.person ?? "-"}</CTableDataCell>
                            <CTableDataCell>{row.provider ?? "-"}</CTableDataCell>
                            <CTableDataCell>{row.shelf ?? "-"}</CTableDataCell>
                            <CTableDataCell>{row.level ?? "-"}</CTableDataCell>
                            <CTableDataCell>{row.availableQuantity ?? 0}</CTableDataCell>
                          </CTableRow>
                        ))
                      )}
                    </CTableBody>
                  </CTable>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={`¿Estás seguro de eliminar el registro ${modalDetails.itemName}?`}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal genérico (placeholder) */}
      <CModal size="xl" alignment="center" visible={false} onClose={() => {}} backdrop="static">
        <CModalHeader>
          <CModalTitle>Modal</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          {/* placeholder content */}
        </CModalBody>
      </CModal>
    </div>
  );
}
